import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { LocalStorageService } from '../local-storage/local-storage.service';

export type Theme = 'light' | 'dark' | 'auto';

const STORAGE_KEY = 'byod.theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
    private readonly document = inject(DOCUMENT);
    private readonly storage = inject(LocalStorageService);

    readonly theme = signal<Theme>(this.readStored());

    readonly resolvedTheme = computed<'light' | 'dark'>(() => {
        const t = this.theme();
        if (t === 'auto') return this.systemPrefersDark() ? 'dark' : 'light';
        return t;
    });

    private readonly systemPrefersDark = signal<boolean>(this.matchesDark());

    constructor() {
        const mql = this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)');
        mql?.addEventListener?.('change', (e) => this.systemPrefersDark.set(e.matches));

        effect(() => {
            const resolved = this.resolvedTheme();
            const html = this.document.documentElement;
            html.setAttribute('data-coreui-theme', resolved);
            html.setAttribute('data-bs-theme', resolved);
            html.style.colorScheme = resolved;
        });
    }

    setTheme(theme: Theme): void {
        this.theme.set(theme);
        this.storage.set(STORAGE_KEY, theme);
    }

    private readStored(): Theme {
        const v = this.storage.get(STORAGE_KEY);
        if (v === 'light' || v === 'dark' || v === 'auto') return v;
        return 'auto';
    }

    private matchesDark(): boolean {
        return !!this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)').matches;
    }
}
